import { Box, Menu } from '@zillow/constellation-10';
import type React from 'react';
import { useCallback } from 'react';
import { Bedrooms } from 'app/shared/types/Bedrooms';
import { PropertyTypes } from 'app/shared/types/PropertyTypes';

import type { CSSProperties } from 'react';
import { logError } from '@zg-rentals/log-error';

interface FilterTrayProps {
  bedrooms: Bedrooms;
  setBedrooms: (bedrooms: Bedrooms) => (dispatch: any, getState: any) => Promise<void>;
  propertyTypes: PropertyTypes;
  setPropertyTypes: (propertyTypes: PropertyTypes) => (dispatch: any, getState: any) => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  isMobile: boolean;
  style?: CSSProperties;
}

const FilterTray: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<FilterTrayProps>>> = (
  props,
) => {
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes, loading, setLoading, isMobile, style } = props;

  const changeBedrooms = useCallback(
    (newBedrooms: Bedrooms) => {
      setLoading(true);
      Promise.resolve(setBedrooms(newBedrooms))
        .then(() => {
          setLoading(false);
          return;
        })
        .catch((error) => {
          logError({
            error,
            errorType: 'Filter',
            context: { details: 'Error while changing bedrooms filter', root: 'components/FilterTray#changeBedrooms' },
          });
          setLoading(false);
        });
    },
    [setBedrooms, setLoading],
  );

  const changePropertyTypes = useCallback(
    (newPropertyTypes: PropertyTypes) => {
      setLoading(true);
      Promise.resolve(setPropertyTypes(newPropertyTypes))
        .then(() => {
          setLoading(false);
          return;
        })
        .catch((error) => {
          logError({
            error,
            errorType: 'Filter',
            context: {
              details: 'Error while changing propertyType filter',
              root: 'components/FilterTray#changePropertyTypes',
            },
          });
          setLoading(false);
        });
    },
    [setPropertyTypes, setLoading],
  );

  const selectAllBedrooms = useCallback(() => changeBedrooms(Bedrooms.ALL), [changeBedrooms]);
  const selectStudio = useCallback(() => changeBedrooms(Bedrooms.STUDIO), [changeBedrooms]);
  const selectOneBedroom = useCallback(() => changeBedrooms(Bedrooms.ONE), [changeBedrooms]);
  const selectTwoBedrooms = useCallback(() => changeBedrooms(Bedrooms.TWO), [changeBedrooms]);
  const selectThreeBedrooms = useCallback(() => changeBedrooms(Bedrooms.THREE), [changeBedrooms]);
  const selectFourPlusBedrooms = useCallback(() => changeBedrooms(Bedrooms.FOURPLUS), [changeBedrooms]);

  const selectAllPropertyTypes = useCallback(() => changePropertyTypes(PropertyTypes.ALL), [changePropertyTypes]);
  const selectHouses = useCallback(() => changePropertyTypes(PropertyTypes.HOUSE), [changePropertyTypes]);
  const selectApartments = useCallback(() => changePropertyTypes(PropertyTypes.APTSANDCONDOS), [changePropertyTypes]);
  const selectTownhouses = useCallback(() => changePropertyTypes(PropertyTypes.TOWNHOUSE), [changePropertyTypes]);

  const menuItemSelectedStyles = { backgroundColor: 'bg.action.soft.default' };

  return (
    // oxlint-disable-next-line constellation/no-raw-spacing-values
    <Box css={{ maxWidth: '381px', marginBottom: '400' }} style={style}>
      <Menu
        trigger={
          <Menu.Button disabled={loading} css={{ height: '900', marginRight: '200' }}>
            {bedrooms}
          </Menu.Button>
        }
        content={
          <>
            <Menu.Item css={bedrooms === Bedrooms.ALL ? menuItemSelectedStyles : undefined} onClick={selectAllBedrooms}>
              All bedrooms
            </Menu.Item>
            <Menu.Item css={bedrooms === Bedrooms.STUDIO ? menuItemSelectedStyles : undefined} onClick={selectStudio}>
              Studio
            </Menu.Item>
            <Menu.Item css={bedrooms === Bedrooms.ONE ? menuItemSelectedStyles : undefined} onClick={selectOneBedroom}>
              1 bedroom
            </Menu.Item>
            <Menu.Item css={bedrooms === Bedrooms.TWO ? menuItemSelectedStyles : undefined} onClick={selectTwoBedrooms}>
              2 bedroom
            </Menu.Item>
            <Menu.Item
              css={bedrooms === Bedrooms.THREE ? menuItemSelectedStyles : undefined}
              onClick={selectThreeBedrooms}
            >
              3 bedroom
            </Menu.Item>
            <Menu.Item
              css={bedrooms === Bedrooms.FOURPLUS ? menuItemSelectedStyles : undefined}
              onClick={selectFourPlusBedrooms}
            >
              4 bedroom +
            </Menu.Item>
          </>
        }
      />
      <Menu
        trigger={
          <Menu.Button disabled={loading} css={{ height: '900' }}>
            {propertyTypes === PropertyTypes.APTSANDCONDOS && isMobile ? 'Apts. & Condos' : propertyTypes}
          </Menu.Button>
        }
        content={
          <>
            <Menu.Item
              css={propertyTypes === PropertyTypes.ALL ? menuItemSelectedStyles : undefined}
              onClick={selectAllPropertyTypes}
            >
              All property types
            </Menu.Item>
            <Menu.Item
              css={propertyTypes === PropertyTypes.HOUSE ? menuItemSelectedStyles : undefined}
              onClick={selectHouses}
            >
              Houses
            </Menu.Item>
            <Menu.Item
              css={propertyTypes === PropertyTypes.APTSANDCONDOS ? menuItemSelectedStyles : undefined}
              onClick={selectApartments}
            >
              Apartments and condos
            </Menu.Item>
            <Menu.Item
              css={propertyTypes === PropertyTypes.TOWNHOUSE ? menuItemSelectedStyles : undefined}
              onClick={selectTownhouses}
            >
              Townhomes
            </Menu.Item>
          </>
        }
      />
    </Box>
  );
};

export default FilterTray;
